/**
 * `active` + `state` (`specs/M6-A1-automation-engine.md` §4.4).
 *
 * Two independent fields, exactly as monday returns them, and the reason both exist is that they
 * disagree on 19 production rows. Reproduced deliberately on the sandbox: switching a card off
 * writes `active:false` while `state` is still `'active'`, and only a later read shows
 * `deactivated` (`discovery/automations/sandbox-experiments.md` E10).
 */
/** Every `state` value observed on the account, plus `null` mapped to `deactivated` on import. */
export const AUTOMATION_STATES = [
    'active',
    'deactivated',
    'auto_disabled_by_user',
    'auto_disabled_by_deleted_resource',
    'auto_disabled_by_column',
    'error',
    'not_connected',
    'one_off',
];
/** i18n keys monday puts in `noticeMessage`; the one free-text value is kept verbatim. */
export const AUTOMATION_NOTICES = {
    creatorDeactivated: 'store.monday.automation.error.creator_deactivated',
    missingColumn: 'store.monday.automation.error.missing_column',
    missingGroup: 'store.monday.automation.error.missing_group',
    notAuthorizedOrDeletedBoard: 'store.monday.automation.error.not_authorized_or_deleted_board',
    creatorUnauthorized: 'store.monday.integration.error.creator_unauthorized',
    /** Ours: a recipe the registry cannot build (`specs/M6-A1` §9). */
    unmappedRecipe: 'ez.automation.unmapped_recipe',
    /** Ours: the trigger mapped but an action did not, so the automation stays off (R7). */
    partialRecipe: 'ez.automation.partial_recipe',
    /** Ours: parked after `AUTOMATION_FAILURE_THRESHOLD` consecutive failed runs. */
    repeatedFailures: 'ez.automation.repeated_failures',
    /** monday's own key for an integration whose app authorisation is missing or revoked
     *  (`state: 'not_connected'`, 5 production rows — `discovery/automations/api/catalog.md` §1). */
    integrationNotConnected: 'store.monday.integration.error.not_connected',
    /** Ours (M6-A4 §6): the recipe mapped, but monday never exported the webhook URL — it lives
     *  in the `webhooks` app's credential store, which `board_automations` does not return. */
    webhookUrlMissing: 'ez.automation.webhook_url_missing',
    /** Ours: a required slot is empty in monday's own config (e.g. a Slack recipe with no
     *  channel — 5 of the 13 Slack rows, all of them already `not_connected` in monday). */
    slotNotConfigured: 'ez.automation.slot_not_configured',
    /**
     * Ours (round 9l): the recipe points at a label, column, group or board the board does not
     * have. monday has a notice for a deleted **column** and a deleted **group** and none at all
     * for a deleted **label**, which is how board *Activities* kept an `active` automation that
     * sets `status` to a label id the column no longer has. The automation is **never** switched
     * off for it — the centre shows this notice and the run history carries the detail.
     */
    danglingReference: 'ez.automation.dangling_reference',
    /**
     * Ours (round 10x, P-278): a block's stored parameters do not parse against the block's own
     * schema — monday wrote a shape the registry does not accept yet. The run fails with
     * `invalid_params` and **only that run**: before P-278 the ZodError escaped the engine, the
     * outbox bridge caught it, and the whole event was dropped, so every other automation on the
     * board missed it too.
     */
    invalidBlockParams: 'ez.automation.invalid_block_params',
};
/** The one gate. `active` alone never runs anything, and neither does `state` alone. */
export function automationRuns(row) {
    return row.active === true && row.state === 'active';
}
/** Import mapping outcome (`specs/M6-A1` §9, R7: nothing is ever dropped). */
export const MAPPING_STATUSES = ['mapped', 'partial', 'unmapped'];
export const RUN_STATUSES = [
    'running',
    'waiting',
    'succeeded',
    'failed',
    'skipped_condition',
    'skipped_loop',
    'skipped_permission',
    'skipped_disabled',
    /**
     * The occurrence was due **before** the board's last sync from monday, so monday itself was
     * the live system when it came round and has already handled it (`docs/11-cutover.md`
     * § "Keeping in sync"). Firing it here would be a second, duplicate notification/action on
     * history. The run is recorded rather than dropped so the history says why nothing happened.
     */
    'skipped_pre_sync',
    'cancelled',
];
export const RUN_STEP_STATUSES = [
    'matched',
    'passed',
    'failed_condition',
    'waiting',
    'succeeded',
    'failed',
    'retrying',
    'skipped',
];
/**
 * Loop protection constants (`specs/M6-A1` §5.5).
 *
 * `AUTOMATION_MAX_DEPTH` is **stricter than monday**, deliberately: on the sandbox an A→B→A pair
 * flipped one item roughly every three seconds for five minutes from a single user write and
 * monday never cut the chain (`sandbox-experiments.md` E7). Ten hops covers every migrated
 * recipe and stops a runaway in seconds.
 */
export const AUTOMATION_MAX_DEPTH = 10;
/** Consecutive failed runs before `markState(id,'error')`. ASSUMED — monday publishes none. */
export const AUTOMATION_FAILURE_THRESHOLD = 3;
/** How stale a due schedule row may be and still fire, flagged `late` (R6). */
export const SCHEDULE_LATE_AFTER_MS = 24 * 60 * 60 * 1000;
/** Belt and braces for the matching index (R3). */
export const AUTOMATION_INDEX_REFRESH_MS = 5 * 60 * 1000;
//# sourceMappingURL=states.js.map